/**
 * RequestRecordsForm — "Get records" form for a GRANTED HIU consent.
 *
 * Allowed scope (R4): the GRANTED scope when consent.grantedScopeReceived, else the
 * requested one. Dates are IST calendar days; the bounds sent are clamped to the scope
 * (and never into the future) so the default To date (= scope end) is always accepted
 * (R8.1, IST/UTC 5.5h mismatch). A failed request leaves the form enabled for retry.
 *
 * One request fetches from every hospital the patient shared (one consent artefact per
 * hospital); the scope offered is the union, and the server narrows each hospital's fetch
 * to what that hospital was allowed to share.
 */
import React, { useRef, useState } from 'react';

import { requestHealthInfo } from '../../services/api/hiuApi';
import { istDateToISOString } from '../../utils/dateIST';
import { OTHER_ORGS } from '../../constants/abdmCopy';
import { hiTypeLabel } from '../../constants/abdmHiTypes';

const toISTDate = (iso) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

export function scopeOf(consent) {
  return consent.grantedScopeReceived
    ? { hiTypes: consent.hiTypes, from: consent.dateFrom, to: consent.dateTo }
    : { hiTypes: consent.requestedHiTypes, from: consent.requestedDateFrom, to: consent.requestedDateTo };
}

export default function RequestRecordsForm({ consent, onSubmitted, isDark }) {
  const scope = scopeOf(consent);
  const minDate = toISTDate(scope.from);
  const maxDate = toISTDate(scope.to);

  // Starts with every allowed type ticked; the user can untick (or tick back) any of them.
  const [hiTypes, setHiTypes] = useState(scope.hiTypes);
  const [dateFrom, setDateFrom] = useState(minDate);
  const [dateTo, setDateTo] = useState(maxDate);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);

  const toggle = (type) => setHiTypes((cur) => (
    cur.includes(type) ? cur.filter((t) => t !== type) : [...cur, type]
  ));

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    // Only types still inside the allowed scope (it can narrow after the form mounted), like the dates below.
    const picked = hiTypes.filter((t) => scope.hiTypes.includes(t));
    if (picked.length === 0 || !dateFrom || !dateTo) {
      setError(OTHER_ORGS.pickTypeAndDates);
      return;
    }
    const scopeFrom = new Date(scope.from).getTime();
    const scopeTo = Math.min(new Date(scope.to).getTime(), Date.now());
    const from = Math.max(new Date(istDateToISOString(dateFrom)).getTime(), scopeFrom);
    // End of the chosen IST day, capped by the scope end (and now).
    const to = Math.min(new Date(istDateToISOString(dateTo)).getTime() + 86399999, scopeTo);
    if (from > to) {
      setError('The date range is outside the range the patient allowed.');
      return;
    }
    inFlight.current = true;
    setRequesting(true);
    setError('');
    try {
      const result = await requestHealthInfo({
        consentArtefactId: consent.consentId,
        dateFrom: new Date(from).toISOString(),
        dateTo: new Date(to).toISOString(),
        hiTypes: picked,
      });
      if (onSubmitted) onSubmitted(result);
    } catch (err) {
      if (err.code === 'OWN_ORGANISATION') setError(OTHER_ORGS.ownOrgText);
      else if (err.code === 'SCOPE_UNKNOWN') setError(OTHER_ORGS.scopeUnknownHint);
      else setError(err.userMessage || OTHER_ORGS.getFailed);
    } finally {
      inFlight.current = false;
      setRequesting(false);
    }
  };

  const inputStyle = {
    flex: 1, padding: '8px 10px', borderRadius: 8, fontSize: 14,
    background: isDark ? '#0f172a' : '#ffffff', color: isDark ? '#f1f5f9' : '#111827',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
  };

  const hospitalNames = (consent.artefacts || []).filter((a) => !a.isOwnOrganisation).map((a) => a.hipName || a.hipId || OTHER_ORGS.organisation);

  return (
    <form onSubmit={submit}>
      {hospitalNames.length > 1 && (
        <p style={{ fontSize: 13, color: isDark ? '#9ca3af' : '#6b7280', margin: '0 0 12px' }}>
          {OTHER_ORGS.fromOrgs(hospitalNames.length, hospitalNames.join(', '))}
        </p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {scope.hiTypes.map((type) => {
          const active = hiTypes.includes(type);
          return (
            <button
              type="button" key={type} onClick={() => toggle(type)} aria-pressed={active}
              style={{
                padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: active ? '#0369a1' : (isDark ? '#1e293b' : '#f1f5f9'),
                color: active ? '#ffffff' : (isDark ? '#e2e8f0' : '#374151'),
                border: `1px solid ${active ? '#0369a1' : (isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db')}`,
              }}
            >
              {hiTypeLabel(type)}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <input
          type="date" aria-label="From date" value={dateFrom} min={minDate} max={maxDate}
          onChange={(e) => setDateFrom(e.target.value)} style={inputStyle}
        />
        <input
          type="date" aria-label="To date" value={dateTo} min={minDate} max={maxDate}
          onChange={(e) => setDateTo(e.target.value)} style={inputStyle}
        />
      </div>
      {error && (
        <p role="alert" style={{ color: isDark ? '#f87171' : '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</p>
      )}
      <button
        type="submit" disabled={requesting}
        style={{
          background: '#0369a1', color: '#ffffff', border: 'none',
          borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600,
          opacity: requesting ? 0.6 : 1, cursor: requesting ? 'not-allowed' : 'pointer',
        }}
      >
        {requesting ? OTHER_ORGS.gettingRecords : OTHER_ORGS.getRecords}
      </button>
    </form>
  );
}
